/**
 * v2 toast — a thin wrapper around Swal.fire's toast mode that
 * bakes in the V2 defaults (top-end position, 3s timer, no
 * confirm button, brand-styled chrome via swal-v2.css's
 * `.swal2-popup.swal2-toast` rules).
 *
 *   import { toast } from '../utils/v2Toast';
 *
 *   toast.success('Setting saved');
 *   toast.info('5 duplicate emails merged');
 *   toast.error('Could not load history');
 *   toast.warning('Rolling back to last good state');
 *
 *   // Same call, explicit tone:
 *   toast('Custom message', { tone: 'info', timer: 5000 });
 *
 * Why a wrapper rather than letting every page reach for Swal's
 * toast API directly? Two reasons:
 *   1. The defaults (position, timer, no confirm) drift over time
 *      when each call site sets them by hand — v1 had bottom-end,
 *      bottom-start, and top-end variants used inconsistently.
 *   2. If we ever replace Swal with a custom v2 Snackbar component,
 *      every call site already routes through this helper — only
 *      one file changes.
 */
import Swal from 'sweetalert2';

const TONE_TO_ICON = {
  success: 'success',
  info:    'info',
  error:   'error',
  warning: 'warning',
  question:'question',
};

function fireToast(message, options = {}) {
  const {
    tone = 'success',
    timer = 3000,
    position = 'top-end',
    ...rest                // target, didOpen, etc. forwarded as-is
  } = options;
  const icon = TONE_TO_ICON[tone] || 'info';
  return Swal.fire({
    toast: true,
    position,
    icon,
    title: message,
    showConfirmButton: false,
    timer,
    timerProgressBar: true,
    ...rest,
  });
}

export function toast(message, options) {
  return fireToast(message, options);
}

// Convenience verbs so call sites read naturally:
//   toast.success('Saved') → green tick, 3s, top-end.
toast.success  = (msg, opts) => fireToast(msg, { ...opts, tone: 'success' });
toast.info     = (msg, opts) => fireToast(msg, { ...opts, tone: 'info' });
toast.error    = (msg, opts) => fireToast(msg, { ...opts, tone: 'error' });
toast.warning  = (msg, opts) => fireToast(msg, { ...opts, tone: 'warning' });
toast.question = (msg, opts) => fireToast(msg, { ...opts, tone: 'question' });

export default toast;
